// Single-row scrollable tablist behaviour shared by the Trend and Audience
// tab strips: overflow fades/chevrons, mouse drag-to-scroll (touch keeps
// native scrolling), roving-tabindex arrow keys and "active tab into view".
//
// Usage: the strip element gets `ref="stripRef"`, `role="tablist"`,
// `@scroll.passive="updateFades"`, `@keydown="onTablistKeydown"` and the four
// pointer handlers; each tab gets `:ref="(el) => setTabRef(key, el)"` and
// `@click="onTabClick(key)"`.
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const DRAG_THRESHOLD_PX = 4; // a mouse drag past this suppresses the tab click
const CHEVRON_STEP = 0.6; // fraction of the visible strip scrolled per chevron click
const STRIP_PAD_PX = 4; // strip padding, kept clear when revealing a tab

/**
 * @param {object} options
 * @param {import('vue').Ref<HTMLElement|null>} options.stripRef
 * @param {() => string[]} options.keys ordered tab keys
 * @param {() => string} options.active current key
 * @param {(key: string) => void} options.select called to change tabs
 */
export function useTabScroll({ stripRef, keys, active, select }) {
	const tabRefs = new Map();
	const fadeLeft = ref(false);
	const fadeRight = ref(false);
	const dragging = ref(false);
	let resizeObserver = null;
	let drag = null; // { startX, startScroll, moved }
	let suppressClick = false;

	function setTabRef(key, el) {
		if (el) tabRefs.set(key, el);
		else tabRefs.delete(key);
	}

	function updateFades() {
		const el = stripRef.value;
		if (!el) {
			fadeLeft.value = false;
			fadeRight.value = false;
			return;
		}
		const overflow = el.scrollWidth - el.clientWidth;
		fadeLeft.value = overflow > 1 && el.scrollLeft > 1;
		fadeRight.value = overflow > 1 && el.scrollLeft < overflow - 1;
	}

	// Scroll the strip (not the page) so the active tab is fully visible.
	function revealActiveTab() {
		const strip = stripRef.value;
		const el = tabRefs.get(active());
		if (strip && el) {
			const left = el.offsetLeft - STRIP_PAD_PX;
			const right = el.offsetLeft + el.offsetWidth + STRIP_PAD_PX;
			if (left < strip.scrollLeft) strip.scrollLeft = left;
			else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth;
		}
		updateFades();
	}

	function scrollStrip(direction) {
		const strip = stripRef.value;
		if (!strip) return;
		strip.scrollBy({ left: direction * strip.clientWidth * CHEVRON_STEP, behavior: 'smooth' });
	}

	// ---- mouse drag-to-scroll
	function onPointerDown(event) {
		suppressClick = false;
		if (event.pointerType !== 'mouse' || event.button !== 0) return;
		const strip = stripRef.value;
		if (!strip || strip.scrollWidth - strip.clientWidth <= 1) return;
		drag = { startX: event.clientX, startScroll: strip.scrollLeft, moved: false };
	}

	function onPointerMove(event) {
		if (!drag || !stripRef.value) return;
		const dx = event.clientX - drag.startX;
		if (!drag.moved && Math.abs(dx) <= DRAG_THRESHOLD_PX) return;
		if (!drag.moved) {
			drag.moved = true;
			dragging.value = true;
			// Keep receiving moves when the pointer leaves the strip mid-drag.
			try {
				stripRef.value.setPointerCapture(event.pointerId);
			} catch (e) {
				// Not all environments allow capture; the drag still works while inside.
			}
		}
		stripRef.value.scrollLeft = drag.startScroll - dx;
	}

	function onPointerUp(event) {
		if (!drag) return;
		if (drag.moved) {
			// The click that follows this pointerup (if any) belongs to the drag.
			suppressClick = true;
			setTimeout(() => { suppressClick = false; }, 0);
			try {
				stripRef.value?.releasePointerCapture(event.pointerId);
			} catch (e) {
				// ignore
			}
		}
		drag = null;
		dragging.value = false;
	}

	function onTabClick(key) {
		if (suppressClick) {
			suppressClick = false;
			return;
		}
		select(key);
	}

	function onTablistKeydown(event) {
		const list = keys();
		const index = list.indexOf(active());
		let next = null;
		if (event.key === 'ArrowRight') next = list[(index + 1) % list.length];
		else if (event.key === 'ArrowLeft') next = list[(index - 1 + list.length) % list.length];
		else if (event.key === 'Home') next = list[0];
		else if (event.key === 'End') next = list[list.length - 1];
		if (!next) return;
		event.preventDefault();
		select(next);
		nextTick(() => tabRefs.get(next)?.focus({ preventScroll: true }));
	}

	onMounted(() => {
		if (typeof ResizeObserver !== 'undefined') resizeObserver = new ResizeObserver(() => nextTick(updateFades));
		if (stripRef.value && resizeObserver) resizeObserver.observe(stripRef.value);
		nextTick(revealActiveTab);
	});
	onBeforeUnmount(() => {
		if (resizeObserver) resizeObserver.disconnect();
	});
	// The strip may mount/unmount (e.g. collapsing into a dropdown); follow it.
	watch(stripRef, (el, old) => {
		if (old && resizeObserver) resizeObserver.unobserve(old);
		if (el && resizeObserver) resizeObserver.observe(el);
		nextTick(revealActiveTab);
	});
	watch(active, () => nextTick(revealActiveTab));

	return {
		fadeLeft,
		fadeRight,
		dragging,
		setTabRef,
		updateFades,
		revealActiveTab,
		scrollStrip,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onTabClick,
		onTablistKeydown,
	};
}
